import { config } from './env';
import { auth } from './firebase';

/**
 * The one place the browser talks to slate-api. Every call carries the
 * signed-in admin's Firebase ID token; the API answers 401 when there is no
 * usable session and 403 (`not_admin`, `email_unverified`) when there is one
 * that is not allowed in.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** A network failure, a CORS rejection, an offline tab. Never a 4xx. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the API. Check your connection and try again.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function idToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    // An expired refresh token (revoked session, password change) lands here.
    return null;
  }
}

function isJson(response: Response): boolean {
  return (response.headers.get('content-type') ?? '').includes('application/json');
}

async function readError(response: Response): Promise<ApiError> {
  if (isJson(response)) {
    try {
      const body = (await response.json()) as Partial<ApiErrorBody>;
      if (body.error?.message) {
        return new ApiError(response.status, body.error.code || 'error', body.error.message, body.error.details);
      }
    } catch {
      // Fall through to the generic message below.
    }
  }
  return new ApiError(
    response.status,
    'error',
    response.status >= 500
      ? 'Something went wrong on the API. Try again in a moment.'
      : `Request failed (${response.status}).`,
  );
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await idToken();
  if (!token) throw new ApiError(401, 'unauthorized', 'Sign in to continue.');

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError(error);
  }

  if (!response.ok) throw await readError(response);

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }
  if (!isJson(response)) return undefined as T;
  return (await response.json()) as T;
}

/** Human-readable text for anything that can come out of a failed call. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}

/** Build a query string, leaving out anything empty. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}
